/**
 * Коммерческий дашборд: сводная аналитика маркетинг + отдел продаж + финансы.
 * Понедельно/помесячно, юнит-экономика (стоимость договора, окупаемость, доли).
 * Виды: карточки / таблица / графики.
 */
import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchCommercialReport, saveCommercialPlan, fetchCommercialExpenseDetail, fetchCommercialContracts, toggleCommercialContract } from "@/api/dashboard";
import type { CommercialRow, CommercialBreakdownRow, CommercialPlan, CommercialContract } from "@/api/dashboard";
import { setBrandFavicon } from "@/lib/setBrandFavicon";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const rub = (v: number) => Math.round(v).toLocaleString("ru-RU") + " ₽";
const int = (v: number) => Math.round(v).toLocaleString("ru-RU");
const pct = (v: number) => (v ?? 0).toFixed(1).replace(".", ",") + " %";
const xX = (v: number) => (v ?? 0).toFixed(2).replace(".", ",") + "×";
const num2 = (v: number) => (v ?? 0).toFixed(1).replace(".", ",") + " м²";
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const MONTHS_RU = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
const periodLabel = (p: string, gran: "weeks" | "months") =>
  gran === "months" ? `${MONTHS_RU[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}` : p.slice(8, 10) + "." + p.slice(5, 7);
// Диапазон дат периода (от–до) — подпись под названием.
const periodRange = (p: string, gran: "weeks" | "months"): string => {
  const dd = (x: Date) => String(x.getDate()).padStart(2, "0") + "." + String(x.getMonth() + 1).padStart(2, "0");
  if (gran === "months") {
    const y = Number(p.slice(0, 4)), m = Number(p.slice(5, 7));
    const last = new Date(y, m, 0).getDate();
    const mm = String(m).padStart(2, "0");
    return `01.${mm} – ${last}.${mm}`;
  }
  const d = new Date(p + "T00:00:00");
  const end = new Date(d); end.setDate(d.getDate() + 6);
  return `${dd(d)} – ${dd(end)}`;
};

const COST_COLORS = { ad_spend: "#4fae4e", marketing_exp: "#7ec97d", sales_exp: "#e0883a", payroll: "#b8860b" };

function Kpi({ label, value, sub, accent, hint }: { label: string; value: string; sub?: string; accent?: boolean; hint?: string }) {
  return (
    <div className="card" style={{ padding: "14px 16px", borderLeft: accent ? "3px solid var(--brick)" : "1px solid var(--line)" }}>
      <div style={{ fontSize: 10.5, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 5 }}>
        {label}
        {hint && <span title={hint} style={{ cursor: "help", border: "1px solid var(--line)", borderRadius: "50%", padding: "0 4px", fontWeight: 700, fontSize: 9.5 }}>?</span>}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 5, fontFamily: "var(--serif)", letterSpacing: "-0.02em" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function PlanFact({ months, plan }: { months: CommercialRow[]; plan: CommercialPlan }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({
    contracts: plan.contracts != null ? String(plan.contracts) : "",
    revenue: plan.revenue != null ? String(plan.revenue) : "",
    max_cost_per_contract: plan.max_cost_per_contract != null ? String(plan.max_cost_per_contract) : "",
    min_payback: plan.min_payback != null ? String(plan.min_payback) : "",
  });
  const saveMut = useMutation({
    mutationFn: () => saveCommercialPlan({
      contracts: num(draft.contracts), revenue: num(draft.revenue),
      max_cost_per_contract: num(draft.max_cost_per_contract), min_payback: num(draft.min_payback),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["commercial"] }); setEdit(false); },
  });
  const P = plan;
  const recent = [...months].slice(-6).reverse();
  const cell = (factTxt: string, ok: boolean | null) => (
    <td className="num tabular" style={{ color: ok == null ? "var(--ink)" : ok ? "#1f7a3f" : "#b23b30", fontWeight: ok != null ? 700 : 400 }}>{factTxt}</td>
  );
  return (
    <div className="card" style={{ marginTop: 18, overflowX: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <h3 className="card-title" style={{ margin: 0 }}>План-факт (месячный план)</h3>
        <button className="btn btn-sm" onClick={() => setEdit(e => !e)}>{edit ? "Скрыть план" : "Редактировать план"}</button>
      </div>
      {edit && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid var(--line)" }}>
          {([["contracts", "Договоров/мес"], ["revenue", "Выручка/мес ₽"], ["max_cost_per_contract", "Макс. стоим. договора ₽"], ["min_payback", "Мин. окупаемость ×"]] as const).map(([k, l]) => (
            <label key={k} style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: "var(--mute)" }}>
              {l}
              <input className="input-sm" value={draft[k]} onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} style={{ width: 130 }}/>
            </label>
          ))}
          <button className="btn btn-sm btn-primary" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>{saveMut.isPending ? "Сохраняю…" : "Сохранить план"}</button>
        </div>
      )}
      {P.contracts == null && P.revenue == null ? (
        <div style={{ color: "var(--mute)", fontSize: 13 }}>План не задан — нажми «Редактировать план».</div>
      ) : (
        <table className="tbl tbl-tight" style={{ width: "100%" }}>
          <thead><tr>
            <th>Месяц</th>
            <th className="num">Договоры (факт/план)</th><th className="num">Выручка (факт/план)</th>
            <th className="num">Стоим. договора</th><th className="num">Окупаемость</th>
          </tr></thead>
          <tbody>
            {recent.map(m => {
              const cOk = P.contracts ? m.contracts >= P.contracts : null;
              const rOk = P.revenue ? m.revenue >= P.revenue : null;
              const costOk = P.max_cost_per_contract ? (m.cost_per_contract > 0 && m.cost_per_contract <= P.max_cost_per_contract) : null;
              const pbOk = P.min_payback ? m.payback >= P.min_payback : null;
              return (
                <tr key={m.period}>
                  <td style={{ whiteSpace: "nowrap" }}>{m.period.slice(5)}.{m.period.slice(0, 4)}</td>
                  {cell(`${int(m.contracts)} / ${P.contracts ? int(P.contracts) : "—"}${P.contracts ? ` · ${(m.contracts / P.contracts * 100).toFixed(0)}%` : ""}`, cOk)}
                  {cell(`${rub(m.revenue)} / ${P.revenue ? rub(P.revenue) : "—"}${P.revenue ? ` · ${(m.revenue / P.revenue * 100).toFixed(0)}%` : ""}`, rOk)}
                  {cell(`${rub(m.cost_per_contract)}${P.max_cost_per_contract ? ` (≤ ${rub(P.max_cost_per_contract)})` : ""}`, costOk)}
                  {cell(`${xX(m.payback)}${P.min_payback ? ` (≥ ${xX(P.min_payback)})` : ""}`, pbOk)}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function num(s: string): number | null { const n = parseFloat(String(s).replace(",", ".")); return s && s.trim() && !isNaN(n) ? n : null; }

function BreakdownCard({ title, rows }: { title: string; rows: CommercialBreakdownRow[] }) {
  const totRev = rows.reduce((s, r) => s + r.revenue, 0) || 1;
  return (
    <div className="card" style={{ overflowX: "auto" }}>
      <h3 className="card-title">{title}</h3>
      {rows.length === 0 ? <div style={{ color: "var(--mute)", fontSize: 13, padding: 8 }}>Нет данных.</div> : (
        <table className="tbl tbl-tight" style={{ width: "100%" }}>
          <thead><tr>
            <th>{title.includes("менедж") ? "Менеджер" : "Регион"}</th>
            <th className="num">Договоры</th><th className="num">Выручка</th><th className="num">Ср. чек</th><th className="num">Доля</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.name}>
                <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</td>
                <td className="num tabular">{int(r.contracts)}</td>
                <td className="num tabular strong">{rub(r.revenue)}</td>
                <td className="num tabular">{r.contracts ? rub(r.avg_check) : "—"}</td>
                <td className="num tabular" style={{ color: "var(--mute)" }}>{(r.revenue / totRev * 100).toFixed(0)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Слайдер: что входит в статью расходов (по категориям/кампаниям + строки).
function ExpenseDrawer({ component, range, onClose }: { component: string; range: { date_from: string; date_to: string }; onClose: () => void }) {
  const q = useQuery({ queryKey: ["commercial-exp", component, range.date_from, range.date_to], queryFn: () => fetchCommercialExpenseDetail(component, range) });
  const [shown, setShown] = useState(false);
  useEffect(() => { setShown(true); const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", onEsc); return () => document.removeEventListener("keydown", onEsc); }, [onClose]);
  const d = q.data;
  const maxAmt = d && d.by_category.length ? Math.max(...d.by_category.map(g => g.amount), 1) : 1;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.32)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(540px,100%)", height: "100%", background: "var(--card,#fff)", boxShadow: "-8px 0 30px rgba(0,0,0,.18)", display: "flex", flexDirection: "column", transform: shown ? "translateX(0)" : "translateX(100%)", transition: "transform .22s ease" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{d?.label || "Статья расходов"}</div>
            <div style={{ fontSize: 12.5, color: "var(--mute)", marginTop: 3 }}>Что входит · {range.date_from} … {range.date_to}</div>
            {d && <div style={{ fontSize: 20, fontWeight: 800, marginTop: 8, fontFamily: "var(--serif)" }}>{rub(d.total)}</div>}
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 22, lineHeight: 1, cursor: "pointer", color: "var(--mute)" }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "14px 20px" }}>
          {q.isLoading ? <div style={{ color: "var(--mute)" }}>Загрузка…</div> : !d ? <div style={{ color: "var(--brick)" }}>Не удалось загрузить.</div> : (<>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>{component === "ad_spend" ? "По кампаниям" : "По категориям"}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
              {d.by_category.map((g, i) => (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12.5, marginBottom: 3 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.category}{g.count > 1 ? ` · ${g.count}` : ""}</span>
                    <strong>{rub(g.amount)}</strong>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: "var(--paper-2,#f0f1f3)" }}><div style={{ height: "100%", width: `${g.amount / maxAmt * 100}%`, borderRadius: 3, background: "var(--brick)" }}/></div>
                </div>
              ))}
              {d.by_category.length === 0 && <div style={{ color: "var(--mute)", fontSize: 13 }}>Нет данных за период.</div>}
            </div>
            {d.items.length > 0 && (<>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Расходы построчно ({d.items.length})</div>
              <table className="tbl tbl-tight" style={{ width: "100%", fontSize: 12 }}>
                <thead><tr><th>Дата</th><th>Категория</th><th>Описание</th><th className="num">Сумма</th></tr></thead>
                <tbody>
                  {d.items.map((it, i) => (
                    <tr key={i}>
                      <td style={{ whiteSpace: "nowrap" }}>{it.date.slice(8, 10)}.{it.date.slice(5, 7)}</td>
                      <td>{it.category}</td>
                      <td style={{ color: "var(--mute)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.description || it.supplier || "—"}</td>
                      <td className="num tabular">{rub(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>)}
          </>)}
        </div>
      </div>
    </div>
  );
}

// Слайдер договоров — включение/исключение из расчёта (как успешные сделки в маркетинге).
function ContractsPanel({ range }: { range: { date_from: string; date_to: string } }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["commercial-contracts", range.date_from, range.date_to], queryFn: () => fetchCommercialContracts(range) });
  const mut = useMutation({
    mutationFn: ({ id, excluded }: { id: number; excluded: boolean }) => toggleCommercialContract(id, excluded),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["commercial-contracts"] }); qc.invalidateQueries({ queryKey: ["commercial"] }); },
  });
  const d = q.data;
  const list: CommercialContract[] = d?.contracts ?? [];
  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <h3 className="card-title" style={{ margin: 0 }}>Договоры — учёт в расчёте</h3>
        {d && <span style={{ fontSize: 12.5, color: "var(--mute)" }}>Учтено <strong style={{ color: "var(--ink)" }}>{d.total}</strong> из {list.length} · сумма <strong style={{ color: "var(--ink)" }}>{rub(d.total_sum)}</strong></span>}
      </div>
      {q.isLoading ? <div style={{ color: "var(--mute)", fontSize: 13 }}>Загрузка…</div> : list.length === 0 ? <div style={{ color: "var(--mute)", fontSize: 13 }}>Договоров за период нет.</div> : (
        <div style={{ overflowX: "auto" }}>
          <table className="tbl tbl-tight" style={{ width: "100%" }}>
            <thead><tr>
              <th>Заявка</th><th>Договор</th><th>Клиент</th><th>Менеджер</th><th>Регион</th>
              <th className="num">Сумма</th><th className="num">Выручка</th><th style={{ textAlign: "center" }}>Amo</th><th style={{ textAlign: "center" }}>Учёт</th>
            </tr></thead>
            <tbody>
              {list.map(c => {
                const dmy = (s?: string | null) => s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(2, 4)}` : "—";
                return (
                <tr key={c.id} style={{ opacity: c.excluded ? 0.45 : 1 }}>
                  <td style={{ whiteSpace: "nowrap", color: "var(--mute)" }}>{dmy(c.lead_created)}</td>
                  <td style={{ whiteSpace: "nowrap" }} title="Дата входа в этап «Договор заключён»">{dmy(c.won_date || c.signed)}</td>
                  <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.client}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{c.manager}</td>
                  <td>{c.region}</td>
                  <td className="num tabular" style={{ textDecoration: c.excluded ? "line-through" : undefined }}>{rub(c.contract_sum)}</td>
                  <td className="num tabular">{c.revenue ? rub(c.revenue) : "—"}</td>
                  <td style={{ textAlign: "center" }}>{c.amo_url ? <a href={c.amo_url} target="_blank" rel="noreferrer" title="Открыть сделку в AmoCRM" style={{ color: "var(--brick)", textDecoration: "none", fontWeight: 700 }}>↗</a> : "—"}</td>
                  <td style={{ textAlign: "center" }}>
                    <button className={`btn btn-sm ${c.excluded ? "" : "btn-primary"}`} disabled={mut.isPending}
                            onClick={() => mut.mutate({ id: c.id, excluded: !c.excluded })}
                            style={c.excluded ? { color: "#1f7a3f", borderColor: "#1f7a3f" } : undefined}
                            title={c.excluded ? "Включить в расчёт" : "Исключить из расчёта"}>
                      {c.excluded ? "Учитывать" : "Учтена"}
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type Col = { key: string; label: string; fmt: (v: number) => string; strong?: boolean; sub?: string; get?: (r: CommercialRow) => number; section?: string; hiddenByDefault?: boolean; calc?: string };
// Значение метрики из строки: спец-резолвер (динамические цели/воронка) или прямое поле.
const metricVal = (c: Col, r: CommercialRow): number => c.get ? c.get(r) : ((r[c.key as keyof CommercialRow] as number) || 0);

// --- Редактируемые формулы показателей ---
type FTok = { t: "f"; k: string } | { t: "o"; o: string } | { t: "n"; v: number };
const FOPS = ["+", "−", "×", "÷"];
const FORMULA_FIELDS: { k: string; label: string }[] = [
  { k: "revenue", label: "Выручка (приходы)" }, { k: "contracts_sum", label: "Сумма договоров" },
  { k: "contracts", label: "Договоры" }, { k: "done_count", label: "Завершено объектов" }, { k: "done_sum", label: "Сумма завершённых" },
  { k: "sqm", label: "Квадратура" }, { k: "ad_spend", label: "Реклама (Я.Директ)" }, { k: "marketing_exp", label: "Расход на маркетинг" },
  { k: "sales_exp", label: "ОП расход" }, { k: "payroll", label: "ЗП ОП" }, { k: "other_exp", label: "Прочее" },
  { k: "unallocated", label: "Производство (себест.)" }, { k: "total_expense", label: "Общий расход" },
  { k: "impressions", label: "Показы" }, { k: "clicks", label: "Клики" }, { k: "leads", label: "Конверсии" },
];
const FIELD_LABEL: Record<string, string> = Object.fromEntries(FORMULA_FIELDS.map(f => [f.k, f.label]));
function fieldVal(k: string, r: CommercialRow): number {
  if (k === "total_expense") return (r.unallocated || 0) + (r.marketing_exp || 0) + (r.sales_exp || 0) + (r.payroll || 0) + (r.other_exp || 0);
  return (r[k as keyof CommercialRow] as number) || 0;
}
function evalFormula(toks: FTok[], r: CommercialRow): number {
  if (!toks.length) return 0;
  const tv = (t: FTok): number => t.t === "f" ? fieldVal(t.k, r) : t.t === "n" ? t.v : 0;
  let acc = tv(toks[0]); let i = 1;
  while (i + 1 < toks.length) {
    const op = toks[i], nx = toks[i + 1];
    if (op.t === "o") { const v = tv(nx); acc = op.o === "+" ? acc + v : op.o === "−" ? acc - v : op.o === "×" ? acc * v : (v ? acc / v : 0); }
    i += 2;
  }
  return acc;
}
const formulaText = (toks: FTok[]): string => toks.map(t => t.t === "f" ? (FIELD_LABEL[t.k] || t.k) : t.t === "n" ? String(t.v) : ` ${t.o} `).join("").trim();
// Динамические столбцы по целям Я.Метрики (конверсии + стоимость конверсии по каждой цели).
function goalCols(goals?: { goal_id: number; name: string }[]): Col[] {
  if (!goals?.length) return [];
  const out: Col[] = [];
  for (const g of goals) {
    const gid = String(g.goal_id);
    out.push({ key: `goal:${gid}`, label: `Конв: ${g.name}`, fmt: int, section: "Цели Я.Метрики", calc: `Я.Директ: конверсии по цели «${g.name}»`, get: r => r.conv_by_goal?.[gid] || 0 });
    out.push({ key: `cpag:${gid}`, label: `Цена: ${g.name}`, fmt: rub, section: "Цели Я.Метрики", hiddenByDefault: true, calc: `Расход Я.Директа ÷ конверсии по цели «${g.name}»`, get: r => { const c = r.conv_by_goal?.[gid] || 0; return c ? r.ad_spend / c : 0; } });
  }
  return out;
}
// Динамические столбцы CRM-воронки (входы в этапы + CR + CPA + звонки) из Amo.
// CR = текущий этап ÷ предыдущий (для 1-го — ÷ клики). CPA = расход ÷ дошедшие до этапа.
function funnelCols(stages?: { stage_id: number; name: string }[]): Col[] {
  if (!stages?.length) return [];
  const SEC = "CRM-воронка (Amo)";
  const out: Col[] = [{ key: "funnel:calls", label: "Звонки", fmt: int, section: SEC, calc: "Amo: звонки (call_in + call_out) за период", get: r => r.funnel?.calls || 0 }];
  stages.forEach((s, i) => {
    const sid = String(s.stage_id);
    const prev = i > 0 ? String(stages[i - 1].stage_id) : null;
    out.push({ key: `funnel:${sid}`, label: s.name, fmt: int, section: SEC, calc: `Amo: входы сделок в этап «${s.name}» по дате события`, get: r => r.funnel?.[sid] || 0 });
    out.push({ key: `fcr:${sid}`, label: `CR → ${s.name}`, fmt: pct, section: SEC, calc: `«${s.name}» ÷ ${prev ? "предыдущий этап" : "клики"} × 100%`, get: r => { const cur = r.funnel?.[sid] || 0; const base = prev ? (r.funnel?.[prev] || 0) : (r.clicks || 0); return base ? cur / base * 100 : 0; } });
    out.push({ key: `fcpa:${sid}`, label: `CPA ${s.name}`, fmt: rub, section: SEC, calc: `Расход Я.Директа ÷ дошедшие до «${s.name}»`, get: r => { const cur = r.funnel?.[sid] || 0; return cur ? (r.ad_spend || 0) / cur : 0; } });
  });
  return out;
}
// Палитра как в отделе маркетинга.
const PALETTE = ["", "#ffd9d9", "#ffe9b0", "#cdeecd", "#cfe2ff", "#ecd6ff", "#ffe0c2"];

// Раскладка под планирование «Кирпичные беседки»: Расходы → Выручка → Объёмы → Прибыль/Маржа,
// далее детальные разделы (Маркетинг, Отдел продаж, Воронка, Юнит, Цели).
const SEC_EXP = "Расходы", SEC_REV = "Выручка", SEC_VOL = "Объёмы", SEC_PROFIT = "Прибыль и маржа",
  SEC_MKT = "Маркетинг", SEC_SALES = "Отдел продаж", SEC_UNIT = "Юнит-экономика";
// Расход на маркетинг = finance-сумма (оплачено). Я.Директ ad_spend — потребление, не отдельная касса.
const _allExp = (r: CommercialRow) => (r.unallocated || 0) + (r.marketing_exp || 0) + (r.sales_exp || 0) + (r.payroll || 0) + (r.other_exp || 0);
const ALL_METRICS: Col[] = [
  // === РАСХОДЫ (как в планировании) ===
  { key: "total_expense", label: "Общий расход", fmt: rub, strong: true, section: SEC_EXP, calc: "Производство + Маркетинг + ОП + Прочее (все расходы периода)", get: _allExp },
  { key: "unallocated", label: "Расход на производство", fmt: rub, section: SEC_EXP, calc: "Себестоимость: материалы/кирпич/работа бригад/ФОТ произв./аутсорс/доставка" },
  { key: "exp_sales", label: "Расход на отдел продаж", fmt: rub, section: SEC_EXP, calc: "ОП расх. + ЗП ОП", get: r => (r.sales_exp || 0) + (r.payroll || 0) },
  { key: "exp_marketing", label: "Расход на маркетинг", fmt: rub, section: SEC_EXP, calc: "Финансы: оплачено на маркетинг (kind=marketing). Я.Директ-расход — потребление, отдельно (эффективность).", get: r => (r.marketing_exp || 0) },
  { key: "other_exp", label: "Прочие расходы", fmt: rub, section: SEC_EXP, calc: "Финансы: расходы категории kind=other (AmoCRM, бухгалтер, телефония и т.п.)" },
  // === ВЫРУЧКА ===
  { key: "revenue", label: "Выручка факт", fmt: rub, strong: true, section: SEC_REV, calc: "Финансы: все приходы (revenue_facts) по дате прихода за период" },
  { key: "contracts_sum", label: "Выручка по договорам", fmt: rub, section: SEC_REV, calc: "Сумма contract_sum по подписанным договорам периода" },
  // === ОБЪЁМЫ ===
  { key: "contracts", label: "Кол-во договоров", fmt: int, strong: true, section: SEC_VOL, calc: "Производство: договоры по дате подписания (искл. исключённые)" },
  { key: "done_count", label: "Завершено объектов", fmt: int, section: SEC_VOL, calc: "Производство: объекты с датой завершения (actual_end) в периоде" },
  { key: "sqm", label: "Квадратура общая", fmt: num2, section: SEC_VOL, calc: "Сумма sqm по завершённым объектам" },
  { key: "sqm_avg", label: "Квадратура средняя", fmt: num2, section: SEC_VOL, calc: "Квадратура ÷ Завершено объектов", get: r => r.done_count ? (r.sqm || 0) / r.done_count : 0 },
  { key: "sqm_price", label: "Стоимость квадрата сред.", fmt: rub, section: SEC_VOL, calc: "Сумма завершённых ÷ Квадратура", get: r => r.sqm ? (r.done_sum || 0) / r.sqm : 0 },
  // === ПРИБЫЛЬ И МАРЖА ===
  { key: "net_profit", label: "Чистая прибыль факт", fmt: rub, strong: true, section: SEC_PROFIT, calc: "Выручка − все расходы (производство+маркетинг+ОП+прочее). Без налогов — нет данных.", get: r => (r.revenue || 0) - _allExp(r) },
  { key: "net_margin", label: "Маржа по факту", fmt: pct, strong: true, section: SEC_PROFIT, calc: "Чистая прибыль ÷ Выручка × 100%", get: r => r.revenue ? ((r.revenue || 0) - _allExp(r)) / r.revenue * 100 : 0 },
  { key: "gross_margin", label: "Маржа производства", fmt: pct, section: SEC_PROFIT, calc: "(Выручка − Себестоимость) ÷ Выручка × 100%" },
  { key: "roi_marketing", label: "Рентабельность маркетинга", fmt: pct, section: SEC_PROFIT, calc: "Выручка ÷ Расход на маркетинг × 100%", get: r => { const m = (r.marketing_exp || 0); return m ? r.revenue / m * 100 : 0; } },
  { key: "roi_sales", label: "Рентабельность отдела продаж", fmt: pct, section: SEC_PROFIT, calc: "Выручка ÷ (ОП + ЗП) × 100%", get: r => { const s = (r.sales_exp || 0) + (r.payroll || 0); return s ? r.revenue / s * 100 : 0; } },
  { key: "roi_production", label: "Рентабельность производства", fmt: pct, section: SEC_PROFIT, calc: "Выручка ÷ Себестоимость × 100%", get: r => { const p = r.unallocated || 0; return p ? r.revenue / p * 100 : 0; } },
  { key: "rev_share_marketing", label: "Доля маркетинга в выручке", fmt: pct, section: SEC_PROFIT, calc: "Расход на маркетинг ÷ Выручка × 100%" },
  { key: "rev_share_sales", label: "Доля ОП в выручке", fmt: pct, section: SEC_PROFIT, calc: "(ОП + ЗП) ÷ Выручка × 100%" },
  // === МАРКЕТИНГ (детально) ===
  { key: "marketing_exp", label: "Расход на маркетинг (оплачено)", fmt: rub, section: SEC_MKT, calc: "Финансы kind=marketing — фактически оплачено на маркетинг. Идёт в Общий расход." },
  { key: "ad_spend", label: "Расход Я.Директа (потреблено)", fmt: rub, section: SEC_MKT, calc: "Я.Директ: реальное потребление баланса (cost_rub). Для эффективности (CPC, стоим. конверсии), в Общий расход НЕ суммируется." },
  { key: "impressions", label: "Показы", fmt: int, section: SEC_MKT, calc: "Я.Директ: показы" },
  { key: "clicks", label: "Клики", fmt: int, section: SEC_MKT, calc: "Я.Директ: клики" },
  { key: "ctr", label: "CTR", fmt: pct, section: SEC_MKT, calc: "Клики ÷ Показы × 100%" },
  { key: "avg_cpc", label: "CPC (цена клика)", fmt: rub, section: SEC_MKT, calc: "Расход ÷ Клики" },
  { key: "leads", label: "Конверсии", fmt: int, section: SEC_MKT, calc: "Я.Директ: конверсии" },
  { key: "cr", label: "CR (конверсия)", fmt: pct, section: SEC_MKT, calc: "Конверсии ÷ Клики × 100%" },
  { key: "cost_per_conv", label: "Стоим. конверсии", fmt: rub, section: SEC_MKT, calc: "Расход ÷ Конверсии" },
  { key: "cpc_marketing", label: "Стоим. договора · маркетинг", fmt: rub, section: SEC_MKT, calc: "(Реклама + Маркетинг) ÷ Договоры" },
  { key: "romi", label: "ROMI", fmt: pct, section: SEC_MKT, calc: "(Выручка − Расход на маркетинг) ÷ Расход на маркетинг × 100%" },
  { key: "marketing_share", label: "Доля маркетинга в расходах", fmt: pct, section: SEC_MKT, calc: "Расход на маркетинг ÷ Общий расход × 100%" },
  // === ОТДЕЛ ПРОДАЖ (детально) ===
  { key: "sales_exp", label: "ОП — расходы", fmt: rub, section: SEC_SALES, calc: "Финансы: расходы kind=sales (кроме ЗП по ведомости)" },
  { key: "payroll", label: "ЗП отдела продаж", fmt: rub, section: SEC_SALES, calc: "Финансы: ЗП по ведомости (payroll-sync)" },
  { key: "avg_check", label: "Средний чек", fmt: rub, section: SEC_SALES, calc: "Сумма договоров ÷ Договоры", get: r => r.contracts ? (r.contracts_sum || 0) / r.contracts : 0 },
  { key: "cost_per_contract", label: "Стоим. договора (всего)", fmt: rub, section: SEC_SALES, calc: "Всего затрат ÷ Договоры" },
  { key: "cpc_sales", label: "Стоим. договора · ОП", fmt: rub, section: SEC_SALES, calc: "(ОП расх. + ЗП ОП) ÷ Договоры" },
  { key: "done_sum", label: "Сумма завершённых", fmt: rub, section: SEC_SALES, calc: "Сумма договоров по завершённым объектам" },
  { key: "sales_share", label: "Доля ОП в расходах", fmt: pct, section: SEC_SALES, calc: "(ОП + ЗП) ÷ Общий расход × 100%" },
  // === ЮНИТ-ЭКОНОМИКА ===
  { key: "payback", label: "Окупаемость", fmt: xX, section: SEC_UNIT, calc: "Выручка ÷ Всего затрат (в иксах)" },
  { key: "roi", label: "ROI", fmt: pct, section: SEC_UNIT, calc: "(Выручка − Всего затрат) ÷ Всего затрат × 100%" },
  { key: "total_cost", label: "Всего затрат (настр.)", fmt: rub, section: SEC_UNIT, calc: "Сумма выбранных статей («В стоимость включать»)" },
];
const DEFAULT_CARDS = ["total_cost", "cost_per_contract", "payback", "romi", "roi", "revenue", "cpc_marketing", "cpc_sales"];
// Маркетинг расх. (положено на кабинет) НЕ суммируем с Рекламой — это предоплаченный баланс, не расход.
const COMPONENTS_LIST: [string, string][] = [["marketing_exp", "Маркетинг"], ["sales_exp", "ОП расх."], ["payroll", "ЗП ОП"], ["other_exp", "Прочее"], ["unallocated", "Производство"]];

// Пересчёт производных метрик с учётом выбранных статей затрат (галочки).
function recomputeRow(r: CommercialRow, included: Set<string>): CommercialRow {
  const comp = (k: keyof CommercialRow) => included.has(k as string) ? (r[k] as number) || 0 : 0;
  // Расход на маркетинг = финансовая сумма (что оплатили), НЕ Я.Директ-потребление (ad_spend — для эффективности).
  const total = comp("marketing_exp") + comp("sales_exp") + comp("payroll") + comp("other_exp") + comp("unallocated");
  const mkt = comp("marketing_exp");
  const sal = comp("sales_exp") + comp("payroll");
  const cn = r.contracts, rev = r.revenue;
  // Маржа считается от ПОЛНЫХ затрат (не зависит от галочек): производство = себестоимость.
  const prod = r.unallocated || 0;
  const commercial = (r.marketing_exp || 0) + (r.sales_exp || 0) + (r.payroll || 0) + (r.other_exp || 0);
  const rawMkt = (r.marketing_exp || 0);
  const rawSal = (r.sales_exp || 0) + (r.payroll || 0);
  const gp = rev - prod;
  const op = rev - prod - commercial;
  return {
    ...r, total_cost: total, marketing_total: mkt, sales_total: sal,
    cost_per_contract: cn ? total / cn : 0, cpc_marketing: cn ? mkt / cn : 0, cpc_sales: cn ? sal / cn : 0,
    cpc_sales_other: cn ? (sal + (r.other_exp || 0)) / cn : 0,
    payback: total ? rev / total : 0, romi: mkt ? (rev - mkt) / mkt * 100 : 0, roi: total ? (rev - total) / total * 100 : 0,
    marketing_share: total ? mkt / total * 100 : 0, sales_share: total ? sal / total * 100 : 0,
    gross_profit: gp, gross_margin: rev ? gp / rev * 100 : 0, op_profit: op, op_margin: rev ? op / rev * 100 : 0,
    rev_share_marketing: rev ? rawMkt / rev * 100 : 0, rev_share_sales: rev ? rawSal / rev * 100 : 0,
  };
}
// Метрики маркетинга (Я.Директ) показываем по умолчанию; скрыты лишь дублирующие агрегаты.
const DEFAULT_HIDDEN_COLS = ["marketing_total", "sales_total", "cpc_sales_other"];
// Главное наверху: продажи (договоры+стоимости) → воронка → маркетинг → приходы/прибыль → юнит → затраты → цели.
const SECTION_ORDER = [SEC_EXP, SEC_REV, SEC_VOL, SEC_PROFIT, SEC_MKT, SEC_SALES, "CRM-воронка (Amo)", SEC_UNIT, "Цели Я.Метрики"];
type Section = { id: string; name: string; color: string };
const SECTION_COLORS = ["#4fae4e", "#2459b8", "#e0883a", "#b23b30", "#7a6bff", "#1fa3a3", "#8d6e63", "#546e7a"];
function seedSections(cols: Col[]): Section[] {
  const names: string[] = [];
  for (const c of cols) { const s = c.section || "Прочее"; if (!names.includes(s)) names.push(s); }
  names.sort((a, b) => ((SECTION_ORDER.indexOf(a) + 1) || 999) - ((SECTION_ORDER.indexOf(b) + 1) || 999));
  return names.map((name, i) => ({ id: name, name, color: SECTION_COLORS[i % SECTION_COLORS.length] }));
}
// Группировка по кастомным (редактируемым) разделам: порядок из sections, привязка из metSec.
function groupByCustomSections(cols: Col[], sections: Section[], metSec: Record<string, string>): [Section, Col[]][] {
  const map = new Map<string, Col[]>(sections.map(s => [s.id, []]));
  const extra = new Map<string, Col[]>();
  for (const c of cols) {
    const sid = metSec[c.key] || c.section || "Прочее";
    if (map.has(sid)) map.get(sid)!.push(c);
    else { if (!extra.has(sid)) extra.set(sid, []); extra.get(sid)!.push(c); }
  }
  const res: [Section, Col[]][] = sections.map(s => [s, map.get(s.id) || []]);
  for (const [sid, list] of extra) res.push([{ id: sid, name: sid, color: "" }, list]);
  return res;
}

function CommercialTable({ rows, total, cols, gran, onGran, defaultHidden = [], storageKey = "commercial_tbl" }: { rows: CommercialRow[]; total?: CommercialRow; cols: Col[]; gran: "weeks" | "months"; onGran?: (g: "weeks" | "months") => void; defaultHidden?: string[]; storageKey?: string }) {
  const SK = storageKey;
  const load = <T,>(s: string, d: T): T => { try { const v = localStorage.getItem(SK + s); return v ? JSON.parse(v) : d; } catch { return d; } };
  const save = (s: string, v: unknown) => { try { localStorage.setItem(SK + s, JSON.stringify(v)); } catch { /* ignore */ } };
  const [hidden, setHidden] = useState<string[]>(() => load("_hide5", [...defaultHidden, ...cols.filter(c => c.hiddenByDefault).map(c => c.key)]));
  const [order, setOrder] = useState<string[]>(() => load("_order", [] as string[]));
  const [colW, setColW] = useState<Record<string, number>>(() => load("_w", {} as Record<string, number>));
  const [colMark, setColMark] = useState<Record<string, string>>(() => load("_mark", {} as Record<string, string>));
  const [cellMark, setCellMark] = useState<Record<string, string>>(() => load("_cellmark", {} as Record<string, string>));
  const [collapsed, setCollapsed] = useState<string[]>(() => load("_collapsed", [] as string[]));
  const toggleCollapse = (s: string) => setCollapsed(p => { const n = p.includes(s) ? p.filter(x => x !== s) : [...p, s]; save("_collapsed", n); return n; });
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null);
  const qmark = (calc?: string) => calc ? (
    <span onMouseEnter={e => setTip({ x: e.clientX, y: e.clientY, text: calc })} onMouseMove={e => setTip(t => t ? { ...t, x: e.clientX, y: e.clientY } : t)} onMouseLeave={() => setTip(null)} onClick={e => e.stopPropagation()}
          style={{ marginLeft: 5, cursor: "help", color: "var(--mute)", fontSize: 10.5, border: "1px solid var(--line)", borderRadius: "50%", padding: "0 4px", fontWeight: 700, display: "inline-block" }}>?</span>
  ) : null;
  const tipNode = tip ? (
    <div style={{ position: "fixed", left: Math.min(tip.x + 12, (typeof window !== "undefined" ? window.innerWidth : 1200) - 300), top: tip.y + 16, zIndex: 2000, background: "#1f2329", color: "#fff", padding: "7px 10px", borderRadius: 7, fontSize: 11.5, lineHeight: 1.45, maxWidth: 280, pointerEvents: "none", boxShadow: "0 6px 20px rgba(0,0,0,.3)" }}>{tip.text}</div>
  ) : null;
  const [paint, setPaint] = useState<string | null>(null);  // активный цвет кисти (или null)
  const [paintOpen, setPaintOpen] = useState(false);
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [transposed, setTransposed] = useState<boolean>(() => load("_t", false));
  const [gearOpen, setGearOpen] = useState(false);
  const gearRef = useRef<HTMLDivElement>(null);
  const resizing = useRef<{ key: string; startX: number; startW: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragSec, setDragSec] = useState<string | null>(null);  // перетаскиваемый раздел
  // Редактируемые разделы.
  const [sections, setSections] = useState<Section[]>(() => load("_sections2", null as Section[] | null) ?? seedSections(cols));
  const [metSec, setMetSec] = useState<Record<string, string>>(() => load("_metsec2", {} as Record<string, string>));
  const saveSections = (n: Section[]) => { setSections(n); save("_sections2", n); };
  const saveMetSec = (n: Record<string, string>) => { setMetSec(n); save("_metsec2", n); };
  const secRename = (id: string, name: string) => saveSections(sections.map(s => s.id === id ? { ...s, name } : s));
  const secColor = (id: string, color: string) => saveSections(sections.map(s => s.id === id ? { ...s, color } : s));
  const secAdd = () => saveSections([...sections, { id: "s" + sections.length + "_" + sections.reduce((a, s) => a + s.name.length, 0), name: "Новый раздел", color: SECTION_COLORS[sections.length % SECTION_COLORS.length] }]);
  const secDelete = (id: string) => {
    const rest = sections.filter(s => s.id !== id);
    if (!rest.length) return;
    const fallback = rest[0].id;
    const nm = { ...metSec };
    for (const c of cols) if ((metSec[c.key] || c.section) === id) nm[c.key] = fallback;
    saveMetSec(nm); saveSections(rest);
  };
  const secMove = (id: string, dir: -1 | 1) => {
    const i = sections.findIndex(s => s.id === id); const j = i + dir;
    if (i < 0 || j < 0 || j >= sections.length) return;
    const n = [...sections];[n[i], n[j]] = [n[j], n[i]]; saveSections(n);
  };
  const secDropReorder = (targetId: string) => {
    if (!dragSec || dragSec === targetId) { setDragSec(null); return; }
    const ids = sections.map(s => s.id); const from = ids.indexOf(dragSec), to = ids.indexOf(targetId);
    const n = [...sections]; n.splice(to, 0, ...n.splice(from, 1)); saveSections(n); setDragSec(null);
  };
  const assignMetric = (key: string, secId: string) => { saveMetSec({ ...metSec, [key]: secId }); };
  // Переименование показателей (своё название поверх дефолтного).
  const [labels, setLabels] = useState<Record<string, string>>(() => load("_labels", {} as Record<string, string>));
  const effLabel = (c: Col) => labels[c.key] ?? c.label;
  const renameMetric = (key: string, name: string) => setLabels(p => { const n = { ...p }; if (name.trim()) n[key] = name; else delete n[key]; save("_labels", n); return n; });
  // Редактируемые формулы: если для метрики задана своя формула — считаем по ней, иначе встроенно.
  const [formulas, setFormulas] = useState<Record<string, FTok[]>>(() => load("_formulas", {} as Record<string, FTok[]>));
  const setFormula = (key: string, toks: FTok[] | null) => setFormulas(p => { const n = { ...p }; if (toks && toks.length) n[key] = toks; else delete n[key]; save("_formulas", n); return n; });
  const mval = (c: Col, r: CommercialRow): number => formulas[c.key] ? evalFormula(formulas[c.key], r) : metricVal(c, r);
  const [fxOpen, setFxOpen] = useState<string | null>(null);  // у какой метрики открыт редактор формулы
  const [fxNum, setFxNum] = useState("");
  const fxAdd = (key: string, tok: FTok) => setFormula(key, [...(formulas[key] || []), tok]);

  // Перетаскивание таблицы за тело = горизонтальная прокрутка (как в маркетинге).
  const onPanDown = (e: React.PointerEvent) => {
    const el = scrollRef.current; if (!el || paint !== null) return;
    if ((e.target as HTMLElement).closest("button,input,th,.col-resize,a,label")) return;
    const start = { x: e.clientX, sl: el.scrollLeft };
    const mv = (ev: PointerEvent) => { el.scrollLeft = start.sl - (ev.clientX - start.x); };
    const up = () => { window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up);
  };
  // Авто-прокрутка к краям при перетаскивании столбца.
  const onColDragOver = (e: React.DragEvent) => {
    const el = scrollRef.current; if (!el || !dragKey) return;
    const r = el.getBoundingClientRect(); const edge = 70;
    if (e.clientX < r.left + edge) el.scrollLeft -= 18;
    else if (e.clientX > r.right - edge) el.scrollLeft += 18;
  };

  useEffect(() => {
    if (!gearOpen) return;
    const onDown = (e: MouseEvent) => { if (gearRef.current && !gearRef.current.contains(e.target as Node)) setGearOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setGearOpen(false); };
    document.addEventListener("mousedown", onDown); document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onEsc); };
  }, [gearOpen]);

  const ordered = useMemo(() => {
    const byKey = new Map(cols.map(c => [c.key as string, c]));
    const seq = order.filter(k => byKey.has(k));
    for (const c of cols) if (!seq.includes(c.key as string)) seq.push(c.key as string);
    return seq.map(k => byKey.get(k)!);
  }, [cols, order]);
  const visible = ordered.filter(c => !hidden.includes(c.key as string));

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const sc = cols.find(c => c.key === sort.key);
    const s = [...rows]; s.sort((a, b) => (((sc ? mval(sc, a) : 0) - (sc ? mval(sc, b) : 0)) || 0) * sort.dir);
    return s;
  }, [rows, sort]);

  const clickSort = (k: string) => setSort(p => p && p.key === k ? (p.dir === -1 ? { key: k, dir: 1 } : null) : { key: k, dir: -1 });
  const toggleHide = (k: string) => setHidden(p => { const n = p.includes(k) ? p.filter(x => x !== k) : [...p, k]; save("_hide5", n); return n; });
  const setMark = (k: string, c: string) => setColMark(p => { const n = { ...p, [k]: c }; if (!c) delete n[k]; save("_mark", n); return n; });
  // Палитра по ячейкам (сохраняется в localStorage, хард-рефреш не стирает).
  const cellId = (colKey: string, period: string) => `${period}|${colKey}`;
  const cellBg = (colKey: string, period: string): string | undefined => cellMark[cellId(colKey, period)] || colMark[colKey] || undefined;
  const paintCell = (colKey: string, period: string) => {
    if (paint === null) return;
    const id = cellId(colKey, period);
    setCellMark(p => { const n = { ...p }; if (paint === "erase" || n[id] === paint) delete n[id]; else n[id] = paint; save("_cellmark", n); return n; });
  };

  const onResizeDown = (e: React.PointerEvent, key: string) => {
    e.preventDefault(); e.stopPropagation();
    resizing.current = { key, startX: e.clientX, startW: colW[key] || (e.currentTarget.parentElement as HTMLElement)?.offsetWidth || 110 };
    const move = (ev: PointerEvent) => { const r = resizing.current; if (!r) return; const w = Math.max(60, Math.round(r.startW + (ev.clientX - r.startX))); setColW(p => ({ ...p, [r.key]: w })); };
    const up = () => { resizing.current = null; setColW(p => { save("_w", p); return p; }); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  const onDrop = (target: string) => {
    if (!dragKey || dragKey === target) { setDragKey(null); return; }
    const seq = ordered.map(c => c.key as string);
    const from = seq.indexOf(dragKey), to = seq.indexOf(target);
    seq.splice(to, 0, ...seq.splice(from, 1));
    setOrder(seq); save("_order", seq); setDragKey(null);
  };

  const periodHdr = gran === "weeks" ? "Неделя" : "Месяц";
  const footStyle: React.CSSProperties = { fontSize: 13.5, fontWeight: 800, borderTop: "2px solid var(--line)" };

  const cw = (k: string, fallback?: number) => colW[k] ?? fallback;
  const resizeHandle = (k: string) => <span onPointerDown={e => onResizeDown(e, k)} style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 7, cursor: "col-resize" }}/>;
  const ctr = { textAlign: "center" as const };

  const controls = (
    <div style={{ display: "flex", gap: 6, alignItems: "center", position: "relative" }}>
      {onGran && (["weeks", "months"] as const).map(g => (
        <button key={g} className={`btn btn-sm ${gran === g ? "btn-primary" : ""}`} onClick={() => onGran(g)}>{g === "weeks" ? "Недели" : "Месяцы"}</button>
      ))}
      {onGran && <span style={{ width: 1, height: 20, background: "var(--line)" }}/>}
      <button className="btn btn-sm" onClick={() => setTransposed(t => { save("_t", !t); return !t; })} title="Поменять оси местами">
        {transposed ? "⤺ Обычный вид" : "⤢ Перевернуть"}
      </button>
      <div style={{ position: "relative" }}>
        <button className={`btn btn-sm ${paint !== null ? "btn-primary" : ""}`} onClick={() => setPaintOpen(o => !o)} title="Подсветить ячейки">
          🖌 Палитра{paint !== null ? " (вкл)" : ""}
        </button>
        {paintOpen && (
          <div style={{ position: "absolute", right: 0, top: "115%", zIndex: 30, background: "var(--card,#fff)", border: "1px solid var(--line)", borderRadius: 12, boxShadow: "0 14px 36px rgba(0,0,0,.16)", padding: 12, width: 210 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Кисть — крась ячейки</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              {PALETTE.filter(Boolean).map(p => (
                <button key={p} onClick={() => setPaint(p)} title="Выбрать цвет"
                        style={{ width: 24, height: 24, borderRadius: "50%", cursor: "pointer", background: p, border: paint === p ? "2px solid var(--ink)" : "1px solid var(--line)" }}/>
              ))}
              <label title="Свой цвет" style={{ width: 24, height: 24, borderRadius: "50%", cursor: "pointer", border: "1px solid var(--line)", overflow: "hidden", position: "relative",
                                                background: "conic-gradient(from 210deg,#ff5c5c,#ffb02e,#ffe24d,#5fd06a,#2ab7ff,#7a6bff,#ff6bd6,#ff5c5c)" }}>
                <input type="color" onChange={e => setPaint(e.target.value)} style={{ opacity: 0, width: "100%", height: "100%", cursor: "pointer", border: "none" }}/>
              </label>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className={`btn btn-sm ${paint === "erase" ? "btn-primary" : ""}`} onClick={() => setPaint("erase")} style={{ flex: 1 }}>Ластик</button>
              <button className="btn btn-sm" onClick={() => setPaint(null)} style={{ flex: 1 }}>Выкл</button>
            </div>
            {paint !== null && <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 8 }}>Кликай по ячейкам, чтобы {paint === "erase" ? "снять цвет" : "подсветить"}.</div>}
          </div>
        )}
      </div>
      <div style={{ position: "relative" }}>
        <button className="btn btn-sm" onClick={() => setGearOpen(o => !o)} title="Настроить показатели и разделы"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: -2 }}><path d="M4 7h8M16 7h4M4 17h4M12 17h8M14 4v6M8 14v6"/></svg>Показатели</button>
        {gearOpen && (() => {
          const tiny: React.CSSProperties = { border: "none", background: "transparent", cursor: "pointer", color: "var(--mute)", fontSize: 12, padding: "0 4px", lineHeight: 1 };
          const fx = (key: string) => formulas[key] || [];
          const renderRow = (c: Col) => {
            const key = c.key, open = fxOpen === key, has = !!formulas[key];
            return (
              <div key={key} style={{ borderRadius: 8, background: dragKey === key ? "#eaf6ee" : (open ? "var(--paper-2,#f6f7f9)" : undefined), padding: open ? "2px 2px 8px" : 0 }}>
                <div draggable onDragStart={() => { setDragKey(key); setDragSec(null); }} onDragOver={e => e.preventDefault()} onDrop={e => { e.stopPropagation(); onDrop(key); }}
                     style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 6px" }}>
                  <span style={{ cursor: "grab", color: "var(--mute)", opacity: 0.4, fontSize: 15, flex: "0 0 auto" }}>⠿</span>
                  <input type="checkbox" checked={!hidden.includes(key)} onChange={() => toggleHide(key)} style={{ cursor: "pointer", flex: "0 0 auto" }}/>
                  <input value={effLabel(c)} onChange={e => renameMetric(key, e.target.value)} title="Название показателя"
                         style={{ flex: 1, minWidth: 0, fontSize: 13, border: "1px solid transparent", background: "transparent", outline: "none", padding: "3px 5px", borderRadius: 6 }}
                         onFocus={e => (e.target.style.border = "1px solid var(--line)")} onBlur={e => (e.target.style.border = "1px solid transparent")}/>
                  <button onClick={() => { setFxOpen(open ? null : key); setFxNum(""); }} title="Как считается (формула)"
                          style={{ ...tiny, fontWeight: 700, color: has ? "var(--brick)" : "var(--mute)", border: `1px solid ${open ? "var(--brick)" : "var(--line)"}`, borderRadius: 6, padding: "2px 6px", fontSize: 11 }}>ƒ{has ? "•" : ""}</button>
                  <div style={{ display: "flex", gap: 3, alignItems: "center", flex: "0 0 auto" }}>
                    {PALETTE.map(p => (
                      <button key={p || "none"} onClick={() => setMark(key, p)} title={p ? "Цвет столбца" : "Без цвета"}
                              style={{ width: 14, height: 14, borderRadius: "50%", cursor: "pointer", padding: 0, background: p || "transparent",
                                       border: colMark[key] === p ? "2px solid var(--ink)" : "1px solid var(--line)", position: "relative", boxSizing: "border-box" }}>
                        {!p && <span style={{ position: "absolute", inset: 0, fontSize: 9, lineHeight: "12px", textAlign: "center", color: "var(--mute)" }}>✕</span>}
                      </button>
                    ))}
                  </div>
                </div>
                {open && (
                  <div style={{ margin: "0 8px", padding: "8px 10px", background: "var(--card,#fff)", border: "1px solid var(--line)", borderRadius: 8 }}>
                    <div style={{ fontSize: 11.5, color: "var(--mute)", marginBottom: 6 }}>
                      {has ? <>Формула: <strong style={{ color: "var(--ink)" }}>{formulaText(fx(key))}</strong></> : <>Сейчас: <strong>встроенный расчёт</strong> ({c.calc || "—"}). Задай свою — считать слева направо.</>}
                    </div>
                    {has && <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                      {fx(key).map((t, i) => <span key={i} style={{ fontSize: 11.5, background: t.t === "o" ? "var(--brick)" : "var(--paper-2,#eef1f4)", color: t.t === "o" ? "#fff" : "var(--ink)", borderRadius: 5, padding: "1px 6px" }}>{t.t === "f" ? (FIELD_LABEL[t.k] || t.k) : t.t === "n" ? t.v : t.o}</span>)}
                      <button onClick={() => setFormula(key, fx(key).slice(0, -1))} title="Удалить последнее" style={{ ...tiny }}>⌫</button>
                    </div>}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
                      <select className="input-sm" value="" onChange={e => { if (e.target.value) fxAdd(key, { t: "f", k: e.target.value }); }} style={{ maxWidth: 150 }}>
                        <option value="">+ показатель</option>
                        {FORMULA_FIELDS.map(f => <option key={f.k} value={f.k}>{f.label}</option>)}
                      </select>
                      {FOPS.map(o => <button key={o} onClick={() => fxAdd(key, { t: "o", o })} className="btn btn-sm" style={{ minWidth: 28, padding: "2px 6px" }}>{o}</button>)}
                      <input className="input-sm" placeholder="число" value={fxNum} onChange={e => setFxNum(e.target.value)} style={{ width: 70 }}/>
                      <button className="btn btn-sm" onClick={() => { const n = parseFloat(fxNum.replace(",", ".")); if (!isNaN(n)) { fxAdd(key, { t: "n", v: n }); setFxNum(""); } }}>+ число</button>
                      {has && <button className="btn btn-sm" onClick={() => setFormula(key, null)} style={{ color: "#b23b30" }}>Сбросить</button>}
                    </div>
                  </div>
                )}
              </div>
            );
          };
          return (
            <div onClick={() => setGearOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.42)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
              <div onClick={e => e.stopPropagation()} style={{ width: "min(720px,100%)", background: "var(--card,#fff)", borderRadius: 16, boxShadow: "0 24px 70px rgba(0,0,0,.32)", maxHeight: "86vh", display: "flex", flexDirection: "column" }}>
                <div style={{ padding: "18px 22px 12px", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700 }}>Настройка показателей и разделов</div>
                    <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 3 }}>Имя показателя ✎ · формула ƒ · цвет · перетащи ⠿ (в т.ч. между разделами). Раздел: имя/цвет/▲▼/×.</div>
                  </div>
                  <button onClick={() => setGearOpen(false)} style={{ border: "none", background: "transparent", fontSize: 24, lineHeight: 1, cursor: "pointer", color: "var(--mute)" }}>×</button>
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "8px 18px 16px" }}>
                  {groupByCustomSections(ordered, sections, metSec).map(([sec, list]) => (
                    <div key={sec.id} onDragOver={e => e.preventDefault()}
                         onDrop={() => { if (dragKey) { assignMetric(dragKey, sec.id); setDragKey(null); } else if (dragSec) secDropReorder(sec.id); }}>
                      <div draggable onDragStart={() => { setDragSec(sec.id); setDragKey(null); }}
                           style={{ display: "flex", alignItems: "center", gap: 6, margin: "12px 2px 5px", paddingTop: 10, borderTop: "1px solid var(--line)" }}>
                        <span style={{ cursor: "grab", color: "var(--mute)", opacity: 0.4, fontSize: 14 }}>⠿</span>
                        <label title="Цвет раздела" style={{ width: 16, height: 16, borderRadius: 4, background: sec.color || "#ccc", border: "1px solid var(--line)", position: "relative", overflow: "hidden", cursor: "pointer", flex: "0 0 auto" }}>
                          <input type="color" value={sec.color || "#4fae4e"} onChange={e => secColor(sec.id, e.target.value)} style={{ opacity: 0, width: "100%", height: "100%", cursor: "pointer", border: "none" }}/>
                        </label>
                        <input value={sec.name} onChange={e => secRename(sec.id, e.target.value)}
                               style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: sec.color || "var(--brick)", border: "1px solid transparent", background: "transparent", outline: "none", padding: "2px 4px", borderRadius: 4 }}
                               onFocus={e => (e.target.style.border = "1px solid var(--line)")} onBlur={e => (e.target.style.border = "1px solid transparent")}/>
                        <span style={{ fontSize: 11, color: "var(--mute)", flex: "0 0 auto" }}>{list.length}</span>
                        <button onClick={() => secMove(sec.id, -1)} title="Выше" style={tiny}>▲</button>
                        <button onClick={() => secMove(sec.id, 1)} title="Ниже" style={tiny}>▼</button>
                        <button onClick={() => secDelete(sec.id)} title="Удалить раздел" style={{ ...tiny, color: "#b23b30", fontSize: 15 }}>×</button>
                      </div>
                      {list.map(renderRow)}
                    </div>
                  ))}
                  <button className="btn btn-sm" onClick={secAdd} style={{ marginTop: 14, width: "100%" }}>+ Добавить раздел</button>
                </div>
                <div style={{ padding: "12px 22px", borderTop: "1px solid var(--line)", textAlign: "right" }}>
                  <button className="btn btn-sm btn-primary" onClick={() => setGearOpen(false)}>Готово</button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );

  // --- Перевёрнутый вид: метрики — строки, периоды — столбцы, ИТОГО сразу после названий ---
  if (transposed) {
    return (
      <div className="card">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>{controls}</div>
        <div ref={scrollRef} onPointerDown={onPanDown} onDragOver={onColDragOver} style={{ overflowX: "auto", cursor: paint !== null ? "crosshair" : "grab" }}>
          <table className="tbl tbl-tight" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
            <thead><tr>
              <th style={{ position: "sticky", left: 0, background: "var(--card, #fff)", zIndex: 2, width: cw("__label__", 170), padding: "9px 12px" }}>Показатель{resizeHandle("__label__")}</th>
              <th className="num" style={{ ...ctr, position: "relative", background: "#f6f7f9", width: cw("__total__", 110), padding: "9px 12px" }}>ИТОГО{resizeHandle("__total__")}</th>
              {sortedRows.map(r => (
                <th key={r.period} className="num" style={{ ...ctr, position: "relative", width: cw(r.period, 104), padding: "9px 12px" }}>
                  <div>{periodLabel(r.period, gran)}</div>
                  <div style={{ fontSize: 9.5, fontWeight: 400, color: "var(--mute)" }}>{periodRange(r.period, gran)}</div>
                  {resizeHandle(r.period)}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {groupByCustomSections(visible, sections, metSec).flatMap(([sec, list]) => {
                const isCol = collapsed.includes(sec.id);
                const header = (
                  <tr key={`sec-${sec.id}`}>
                    <td colSpan={2 + sortedRows.length} onClick={() => toggleCollapse(sec.id)}
                        style={{ position: "sticky", left: 0, background: sec.color ? sec.color + "22" : "var(--paper-2,#eef1f4)", fontSize: 11.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4, color: sec.color || "var(--brick)", padding: "9px 12px", cursor: "pointer", userSelect: "none", borderTop: "1px solid var(--line)", borderLeft: sec.color ? `3px solid ${sec.color}` : undefined }}>
                      <span style={{ display: "inline-block", width: 14, opacity: 0.7 }}>{isCol ? "▸" : "▾"}</span>{sec.name}
                      <span style={{ fontWeight: 400, color: "var(--mute)", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>· {list.length}</span>
                    </td>
                  </tr>
                );
                if (isCol) return [header];
                return [header, ...list.map(c => (
                  <tr key={c.key as string}>
                    <td draggable onDragStart={() => setDragKey(c.key)} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(c.key)}
                        style={{ position: "sticky", left: 0, background: cellBg(c.key, "__label__") || "var(--card, #fff)", zIndex: 1, fontWeight: c.strong ? 700 : 500, whiteSpace: "nowrap", width: cw("__label__", 170), cursor: "grab", padding: "7px 12px 7px 22px" }}>
                      <span style={{ opacity: 0.35, marginRight: 6 }}>⠿</span>{effLabel(c)}
                      {qmark(c.calc)}
                    </td>
                    <td onClick={() => paintCell(c.key, "__total__")} className="num tabular strong" style={{ ...ctr, background: cellBg(c.key, "__total__") || "#f6f7f9", width: cw("__total__", 110), cursor: paint !== null ? "crosshair" : undefined, padding: "7px 12px" }}>{total ? c.fmt(mval(c, total)) : "—"}</td>
                    {sortedRows.map(r => <td key={r.period} onClick={() => paintCell(c.key, r.period)} className={`num tabular${c.strong ? " strong" : ""}`} style={{ ...ctr, background: cellBg(c.key, r.period), width: cw(r.period, 96), cursor: paint !== null ? "crosshair" : undefined, padding: "7px 12px" }}>{c.fmt(mval(c, r))}</td>)}
                  </tr>
                ))];
              })}
            </tbody>
          </table>
        </div>
        {tipNode}
      </div>
    );
  }

  // --- Обычный вид: периоды — строки, метрики — столбцы (ресайз/сорт/перетаскивание/палитра) ---
  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>{controls}</div>
      <div ref={scrollRef} onPointerDown={onPanDown} onDragOver={onColDragOver} style={{ overflowX: "auto", cursor: paint !== null ? "crosshair" : "grab" }}>
        <table className="tbl tbl-tight" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
          <thead><tr>
            <th style={{ position: "sticky", left: 0, background: "var(--card, #fff)", zIndex: 2, whiteSpace: "nowrap" }}>{periodHdr}</th>
            {visible.map(c => (
              <th key={c.key as string} draggable onDragStart={() => setDragKey(c.key as string)} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(c.key as string)}
                  className="num" onClick={() => clickSort(c.key as string)}
                  style={{ ...ctr, position: "relative", cursor: "pointer", width: colW[c.key as string], background: colMark[c.key as string] || (dragKey === c.key ? "#eaf6ee" : undefined), userSelect: "none", whiteSpace: "nowrap" }}>
                {effLabel(c)}{sort?.key === c.key ? (sort.dir === -1 ? " ↓" : " ↑") : ""}
                {qmark(c.calc)}
                {resizeHandle(c.key as string)}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {sortedRows.map(r => (
              <tr key={r.period}>
                <td style={{ position: "sticky", left: 0, background: "var(--card, #fff)", zIndex: 1, whiteSpace: "nowrap" }}>
                  <div style={{ fontWeight: 600 }}>{periodLabel(r.period, gran)}</div>
                  <div style={{ fontSize: 10, fontWeight: 400, color: "var(--mute)" }}>{periodRange(r.period, gran)}</div>
                </td>
                {visible.map(c => <td key={c.key as string} onClick={() => paintCell(c.key, r.period)} className={`num tabular${c.strong ? " strong" : ""}`} style={{ ...ctr, background: cellBg(c.key, r.period), width: colW[c.key as string], cursor: paint !== null ? "crosshair" : undefined }}>{c.fmt(mval(c, r))}</td>)}
              </tr>
            ))}
          </tbody>
          {total && (
            <tfoot><tr>
              <td className="strong" style={{ ...footStyle, position: "sticky", left: 0, background: "var(--card, #fff)" }}>ИТОГО</td>
              {visible.map(c => <td key={c.key as string} onClick={() => paintCell(c.key, "__total__")} className="num tabular strong" style={{ ...footStyle, ...ctr, background: cellBg(c.key, "__total__"), cursor: paint !== null ? "crosshair" : undefined }}>{c.fmt(mval(c, total))}</td>)}
            </tr></tfoot>
          )}
        </table>
      </div>
      {tipNode}
    </div>
  );
}

export function CommercialPage() {
  useEffect(() => { document.title = "Коммерческий дашборд · Кирпичные беседки"; setBrandFavicon("commercial"); }, []);
  const [days, setDays] = useState(90);
  const [gran, setGran] = useState<"weeks" | "months">("weeks");
  const [view, setView] = useState<"cards" | "table" | "chart">("chart");
  const [cardKeys, setCardKeys] = useState<string[]>(() => { try { const s = localStorage.getItem("commercial_cards"); if (s) return JSON.parse(s); } catch { /* ignore */ } return DEFAULT_CARDS; });
  const [cardPickerOpen, setCardPickerOpen] = useState(false);
  const [detailComp, setDetailComp] = useState<string | null>(null);
  const cardPickRef = useRef<HTMLDivElement>(null);
  const toggleCard = (k: string) => setCardKeys(prev => { const n = prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]; try { localStorage.setItem("commercial_cards", JSON.stringify(n)); } catch { /* ignore */ } return n; });
  useEffect(() => {
    if (!cardPickerOpen) return;
    const onDown = (e: MouseEvent) => { if (cardPickRef.current && !cardPickRef.current.contains(e.target as Node)) setCardPickerOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [cardPickerOpen]);
  const to = new Date();
  const from = new Date(); from.setDate(from.getDate() - (days - 1));
  const range = { date_from: iso(from), date_to: iso(to) };
  const q = useQuery({ queryKey: ["commercial", range.date_from, range.date_to], queryFn: () => fetchCommercialReport(range) });
  // Состав «общей коммерческой стоимости» — галочки (мгновенный пересчёт на фронте).
  const [included, setIncluded] = useState<Set<string>>(() => {
    try { const s = localStorage.getItem("commercial_cost_components"); if (s) return new Set(JSON.parse(s)); } catch { /* ignore */ }
    return new Set(["ad_spend", "marketing_exp", "sales_exp", "payroll"]);
  });
  const toggleComp = (k: string) => setIncluded(prev => {
    const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k);
    try { localStorage.setItem("commercial_cost_components", JSON.stringify([...n])); } catch { /* ignore */ }
    return n;
  });
  const recompute = (r: CommercialRow): CommercialRow => recomputeRow(r, included);
  const data = q.data;
  const rows: CommercialRow[] = data ? data[gran].map(recompute) : [];
  const T = data ? recompute(data.total) : undefined;
  const COMPONENTS: [string, string][] = [
    ["ad_spend", "Реклама"], ["marketing_exp", "Маркетинг расх."], ["sales_exp", "ОП расх."], ["payroll", "ЗП ОП"], ["other_exp", "Прочее"], ["unallocated", "Производство"],
  ];

  const chartData = rows.map(r => ({
    p: periodLabel(r.period, gran),
    "Реклама": r.ad_spend, "Маркетинг·расх": r.marketing_exp, "ОП·расх": r.sales_exp, "ЗП ОП": r.payroll,
    "Выручка": r.revenue, "Стоим. договора": r.cost_per_contract, "Окупаемость": r.payback, "Договоры": r.contracts,
  }));

  const COLS = ALL_METRICS;

  return (
    <div style={{ padding: 16, maxWidth: 1320, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Коммерческий дашборд</h1>
          <div style={{ fontSize: 12.5, color: "var(--mute)", marginTop: 4 }}>Маркетинг + отдел продаж + финансы · юнит-экономика</div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <select className="input-sm" value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={30}>30 дней</option><option value={60}>60 дней</option>
            <option value={90}>90 дней</option><option value={180}>180 дней</option>
          </select>
          {(["weeks", "months"] as const).map(g => (
            <button key={g} className={`btn btn-sm ${gran === g ? "btn-primary" : ""}`} onClick={() => setGran(g)}>{g === "weeks" ? "Недели" : "Месяцы"}</button>
          ))}
          <span style={{ width: 1, height: 22, background: "var(--line)" }}/>
          {([["cards", "Карточки"], ["table", "Таблица"], ["chart", "Графики"]] as const).map(([v, l]) => (
            <button key={v} className={`btn btn-sm ${view === v ? "btn-primary" : ""}`} onClick={() => setView(v)}>{l}</button>
          ))}
        </div>
      </div>

      {q.isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40, color: "var(--mute)" }}>Загрузка…</div>
      ) : !data ? (
        <div style={{ color: "var(--brick)", padding: 20 }}>Не удалось загрузить.</div>
      ) : (<>
        {/* Состав общей коммерческой стоимости + клик по статье = что входит */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14, padding: "8px 12px", background: "var(--paper-2, #f6f7f9)", borderRadius: 10, fontSize: 12.5 }}>
          <span style={{ color: "var(--mute)", fontWeight: 600 }}>В стоимость включать:</span>
          {COMPONENTS.map(([k, l]) => (
            <span key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <input type="checkbox" checked={included.has(k)} onChange={() => toggleComp(k)} title="Включать в общую стоимость" style={{ cursor: "pointer" }}/>
              <button onClick={() => setDetailComp(k)} title="Показать, что входит в статью"
                      style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 12.5, color: "var(--ink)", padding: 0, textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}>{l}</button>
            </span>
          ))}
          <span style={{ color: "var(--mute)", fontSize: 11 }}>· клик по статье — что в неё входит</span>
        </div>

        {/* Сводные KPI — настраиваемые карточки */}
        {T && (<>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4 }}>Ключевые показатели</span>
            <div ref={cardPickRef} style={{ position: "relative" }}>
              <button className="btn btn-sm" onClick={() => setCardPickerOpen(o => !o)} title="Выбрать карточки"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: -2 }}><path d="M4 7h8M16 7h4M4 17h4M12 17h8M14 4v6M8 14v6"/></svg>Карточки</button>
              {cardPickerOpen && (
                <div style={{ position: "absolute", right: 0, top: "110%", zIndex: 30, background: "var(--card, #fff)", border: "1px solid var(--line)", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,.12)", padding: 10, width: 290, maxHeight: 440, overflowY: "auto" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Какие карточки выводить</div>
                  {ALL_METRICS.map(m => (
                    <label key={m.key as string} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, padding: "4px 2px", cursor: "pointer" }}>
                      <input type="checkbox" checked={cardKeys.includes(m.key as string)} onChange={() => toggleCard(m.key as string)}/>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 18 }}>
            {cardKeys.map(k => {
              const m = ALL_METRICS.find(x => x.key === k);
              if (!m) return null;
              const sub = m.sub
                || (k === "cost_per_contract" ? `${T.contracts} договоров` : "")
                || (k === "cost_per_conv" ? `${T.leads} конверсий` : "")
                || (k === "marketing_total" ? pct(T.marketing_share) + " затрат" : "")
                || (k === "sales_total" ? pct(T.sales_share) + " затрат" : "");
              return <Kpi key={k} label={m.label} value={m.fmt(metricVal(m, T))} sub={sub || undefined} accent={m.strong} hint={m.calc}/>;
            })}
          </div>
        </>)}

        {view === "chart" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
            <div className="card">
              <h3 className="card-title">Затраты (стопка) и выручка по {gran === "weeks" ? "неделям" : "месяцам"}</h3>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -4 }}>
                  <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false}/>
                  <XAxis dataKey="p" tick={{ fontSize: 10, fill: "var(--mute)" }} tickLine={false}/>
                  <YAxis yAxisId="L" tick={{ fontSize: 10, fill: "var(--mute)" }} tickLine={false} axisLine={false} width={54} tickFormatter={(v: number) => int(v / 1000) + "к"}/>
                  <YAxis yAxisId="R" orientation="right" tick={{ fontSize: 10, fill: "var(--mute)" }} tickLine={false} axisLine={false} width={54} tickFormatter={(v: number) => int(v / 1000) + "к"}/>
                  <Tooltip formatter={(v: any) => rub(Number(v))} contentStyle={{ fontSize: 12, borderRadius: 8 }}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                  <Bar yAxisId="L" dataKey="Реклама" stackId="c" fill={COST_COLORS.ad_spend}/>
                  <Bar yAxisId="L" dataKey="Маркетинг·расх" stackId="c" fill={COST_COLORS.marketing_exp}/>
                  <Bar yAxisId="L" dataKey="ОП·расх" stackId="c" fill={COST_COLORS.sales_exp}/>
                  <Bar yAxisId="L" dataKey="ЗП ОП" stackId="c" fill={COST_COLORS.payroll} radius={[4, 4, 0, 0]}/>
                  <Line yAxisId="R" type="monotone" dataKey="Выручка" stroke="#2459b8" strokeWidth={2.4} dot={false}/>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 className="card-title">Стоимость договора и окупаемость</h3>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -4 }}>
                  <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false}/>
                  <XAxis dataKey="p" tick={{ fontSize: 10, fill: "var(--mute)" }} tickLine={false}/>
                  <YAxis yAxisId="L" tick={{ fontSize: 10, fill: "var(--mute)" }} tickLine={false} axisLine={false} width={54} tickFormatter={(v: number) => int(v / 1000) + "к"}/>
                  <YAxis yAxisId="R" orientation="right" tick={{ fontSize: 10, fill: "var(--mute)" }} tickLine={false} axisLine={false} width={40}/>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                           formatter={(v: any, n: any) => n === "Окупаемость" ? xX(Number(v)) : rub(Number(v))}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                  <Bar yAxisId="L" dataKey="Стоим. договора" fill="#b23b30" radius={[4, 4, 0, 0]}/>
                  <Line yAxisId="R" type="monotone" dataKey="Окупаемость" stroke="#4fae4e" strokeWidth={2.4} dot={false}/>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {view === "table" && <CommercialTable rows={rows} total={T} cols={[...COLS, ...goalCols(data.goals), ...funnelCols(data.funnel_stages)]} gran={gran} onGran={setGran} defaultHidden={DEFAULT_HIDDEN_COLS}/>}

        {view === "cards" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {[...rows].reverse().map(r => (
              <div key={r.period} className="card">
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{gran === "weeks" ? "Неделя " : ""}{periodLabel(r.period, gran)}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5 }}>
                  {[
                    ["Всего затрат", rub(r.total_cost), true],
                    ["Стоимость договора", rub(r.cost_per_contract), true],
                    ["Окупаемость", xX(r.payback), true],
                    ["Договоры", int(r.contracts), false],
                    ["Выручка", rub(r.revenue), false],
                    ["Реклама", rub(r.ad_spend), false],
                    ["ЗП ОП", rub(r.payroll), false],
                    ["Доля маркетинга", pct(r.marketing_share), false],
                  ].map(([l, v, s], i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ color: "var(--mute)" }}>{l}</span>
                      <strong style={{ color: s ? "var(--ink)" : "var(--ink-2)" }}>{v}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* План-факт по месяцам */}
        <PlanFact months={data.months.map(recompute)} plan={data.plan ?? {}}/>

        {/* Разрезы по менеджерам и регионам */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16, marginTop: 18 }}>
          <BreakdownCard title="По менеджерам" rows={data.by_manager ?? []}/>
          <BreakdownCard title="По регионам" rows={data.by_region ?? []}/>
        </div>

        {/* Договоры — включение/исключение из расчёта */}
        <ContractsPanel range={range}/>
      </>)}

      {detailComp && <ExpenseDrawer component={detailComp} range={range} onClose={() => setDetailComp(null)}/>}
    </div>
  );
}

// Вторая страница — «Подробная аналитика»: только большая таблица (переворот, drag-scroll,
// перетаскивание/скрытие/ресайз столбцов, палитра, ИТОГО) + статьи расходов слайдером.
export function CommercialAnalyticsPage() {
  useEffect(() => { document.title = "Подробная аналитика · Коммерческий"; setBrandFavicon("commercial"); }, []);
  const [days, setDays] = useState(90);
  const [gran, setGran] = useState<"weeks" | "months">("weeks");
  const [detailComp, setDetailComp] = useState<string | null>(null);
  const [included, setIncluded] = useState<Set<string>>(() => {
    try { const s = localStorage.getItem("commercial_cost_components"); if (s) return new Set(JSON.parse(s)); } catch { /* ignore */ }
    return new Set(["ad_spend", "marketing_exp", "sales_exp", "payroll"]);
  });
  const toggleComp = (k: string) => setIncluded(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); try { localStorage.setItem("commercial_cost_components", JSON.stringify([...n])); } catch { /* ignore */ } return n; });
  const [presets, setPresets] = useState<{ id: string; name: string }[]>(() => { try { return JSON.parse(localStorage.getItem("commercial_presets") || "[]"); } catch { return []; } });
  const savePresets = (n: { id: string; name: string }[]) => { setPresets(n); try { localStorage.setItem("commercial_presets", JSON.stringify(n)); } catch { /* ignore */ } };
  const addPreset = () => { const name = (window.prompt("Название сохранённой таблицы:", "Новый вид") || "").trim(); if (name) savePresets([...presets, { id: String(Date.now()), name }]); };
  const to = new Date(); const from = new Date(); from.setDate(from.getDate() - (days - 1));
  const range = { date_from: iso(from), date_to: iso(to) };
  const q = useQuery({ queryKey: ["commercial", range.date_from, range.date_to], queryFn: () => fetchCommercialReport(range) });
  const data = q.data;
  const rows: CommercialRow[] = data ? data[gran].map(r => recomputeRow(r, included)) : [];
  const T = data ? recomputeRow(data.total, included) : undefined;

  return (
    <div style={{ padding: "14px 14px", width: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Подробная аналитика</h1>
          <div style={{ fontSize: 12.5, color: "var(--mute)", marginTop: 4 }}>Все показатели по периодам · переворот, перетаскивание столбцов, статьи расходов</div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <select className="input-sm" value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={30}>30 дней</option><option value={60}>60 дней</option><option value={90}>90 дней</option><option value={180}>180 дней</option>
          </select>
          {(["weeks", "months"] as const).map(g => (
            <button key={g} className={`btn btn-sm ${gran === g ? "btn-primary" : ""}`} onClick={() => setGran(g)}>{g === "weeks" ? "Недели" : "Месяцы"}</button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14, padding: "8px 12px", background: "var(--paper-2, #f6f7f9)", borderRadius: 10, fontSize: 12.5 }}>
        <span style={{ color: "var(--mute)", fontWeight: 600 }}>В стоимость включать:</span>
        {COMPONENTS_LIST.map(([k, l]) => (
          <span key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <input type="checkbox" checked={included.has(k)} onChange={() => toggleComp(k)} title="Включать в общую стоимость" style={{ cursor: "pointer" }}/>
            <button onClick={() => setDetailComp(k)} title="Показать, что входит в статью"
                    style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 12.5, color: "var(--ink)", padding: 0, textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}>{l}</button>
          </span>
        ))}
        <span style={{ color: "var(--mute)", fontSize: 11 }}>· клик по статье — что в неё входит</span>
      </div>

      {q.isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40, color: "var(--mute)" }}>Загрузка…</div>
      ) : !data ? (
        <div style={{ color: "var(--brick)", padding: 20 }}>Не удалось загрузить.</div>
      ) : (<>
        <CommercialTable rows={rows} total={T} cols={[...ALL_METRICS, ...goalCols(data.goals), ...funnelCols(data.funnel_stages)]} gran={gran} onGran={setGran}/>

        {/* Сохранённые виды — отдельные таблицы ниже (каждая со своим набором/порядком столбцов) */}
        <div style={{ marginTop: 14 }}>
          <button className="btn btn-sm" onClick={addPreset} title="Сохранить ещё одну таблицу со своим набором столбцов">+ Сохранить вид (доп. таблица)</button>
        </div>
        {presets.map(p => (
          <div key={p.id} style={{ marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{p.name}</h3>
              <button className="btn btn-sm" onClick={() => savePresets(presets.filter(x => x.id !== p.id))} title="Удалить таблицу">✕ Удалить</button>
            </div>
            <CommercialTable rows={rows} total={T} cols={[...ALL_METRICS, ...goalCols(data.goals), ...funnelCols(data.funnel_stages)]} gran={gran} onGran={setGran} storageKey={`commercial_tbl_${p.id}`} defaultHidden={DEFAULT_HIDDEN_COLS}/>
          </div>
        ))}
      </>)}

      {data && <ContractsPanel range={range}/>}

      {detailComp && <ExpenseDrawer component={detailComp} range={range} onClose={() => setDetailComp(null)}/>}
    </div>
  );
}

export default CommercialPage;
